<template>
  <div class="dash-page" :class="{ 'dark': isDarkMode }">
    <!-- Header -->
    <header class="sticky top-0 z-50 bg-white/80 dark:bg-gray-950/80 backdrop-blur-sm border-b border-zinc-100 dark:border-zinc-800">
      <div class="dash-header-inner">
        <router-link to="/" class="logo-link flex items-center">
          <img :src="logoImage" alt="cv-tool.com Logo" class="hidden sm:block h-7 w-auto" />
          <img src="../assets/icons/mobile-logo.png" alt="cv-tool.com Logo" class="block sm:hidden h-7 w-auto" />
        </router-link>
        <UserDropdown
          :current-user="{ full_name: userName, email: userEmail }"
          @logout="handleLogout"
        />
      </div>
    </header>

    <!-- Email confirmation banner -->
    <div v-if="showConfirmationBanner" class="confirmation-banner">
      <div class="confirmation-banner-content">
        <svg class="w-5 h-5 text-amber-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
        <span class="confirmation-banner-text">
          Confirm your email at <strong>{{ userEmail }}</strong> to keep access to your saved CVs.
        </span>
        <span v-if="resendMessage" class="confirmation-banner-feedback">{{ resendMessage }}</span>
        <button v-else @click="resendConfirmationEmail" :disabled="isResendingConfirmation" class="confirmation-banner-btn">
          {{ isResendingConfirmation ? 'Sending…' : 'Resend email' }}
        </button>
        <button @click="showConfirmationBanner = false" class="confirmation-banner-dismiss" aria-label="Dismiss">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="isLoading" class="dash-loading">
      <div class="loading-spinner"></div>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="dash-error">
      <p class="text-red-500 mb-3">{{ error }}</p>
      <button @click="fetchDashboardData" class="btn-secondary">Try Again</button>
    </div>

    <!-- Main two-column layout -->
    <div v-else class="dash-body">

      <!-- ── LEFT: CV LIST ── -->
      <main class="dash-main">
        <div class="dash-main-header">
          <h2 class="dash-main-title">Your CVs <span class="dash-cv-count">{{ totalCvs }}</span></h2>
        </div>

        <!-- Empty state -->
        <div v-if="allCvs.length === 0" class="cv-empty">
          <svg class="w-10 h-10 text-gray-300 dark:text-gray-600 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <p class="text-sm text-gray-400 dark:text-gray-500">No CVs yet — create your first one.</p>
          <button @click="createNewCv" class="btn-primary mt-4 text-sm">Create CV</button>
        </div>

        <!-- CV cards -->
        <div v-else class="cv-list">
          <template v-for="group in cvsByMonth" :key="group.label">
            <p class="cv-month-label">{{ group.label }}</p>
            <div class="cv-grid">
              <div v-for="cv in group.cvs" :key="cv.id" class="cv-card">
                <!-- Card top: target + preview -->
                <div class="cv-card-top" @click="editCv(cv.id)">
                  <div class="cv-card-target">
                    <div class="flex items-start justify-between gap-2">
                      <p class="cv-company">{{ cv.target_company || 'No target company' }}</p>
                      <div class="cv-card-actions-top flex-shrink-0">
                        <button @click.stop="openEditModal(cv.id)" class="action-btn edit-btn" title="Edit details">
                          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                          </svg>
                        </button>
                        <button @click.stop="openDeleteConfirmModal(cv)" class="action-btn delete-btn" title="Delete">
                          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                    <p class="cv-role">{{ cv.target_role || 'No target role' }}</p>
                    <p v-if="cv.description" class="cv-description">{{ cv.description }}</p>
                  </div>

                  <!-- Content preview -->
                  <div v-if="cv.experience_preview?.length || cv.education_preview?.length" class="cv-preview-section">
                    <!-- Experience -->
                    <div v-if="cv.experience_preview?.length" class="cv-preview-block">
                      <p class="cv-preview-label">Experience</p>
                      <ul class="cv-preview-list">
                        <li v-for="e in cv.experience_preview" :key="e.company" class="cv-preview-item">
                          <span class="font-medium text-zinc-700 dark:text-zinc-200">{{ e.job_title }}</span>
                          <span v-if="e.company" class="text-zinc-400 dark:text-zinc-500"> · {{ e.company }}</span>
                        </li>
                      </ul>
                    </div>
                    <!-- Education -->
                    <div v-if="cv.education_preview?.length" class="cv-preview-block">
                      <p class="cv-preview-label">Education</p>
                      <ul class="cv-preview-list">
                        <li v-for="e in cv.education_preview" :key="e.institution" class="cv-preview-item">
                          <span class="font-medium text-zinc-700 dark:text-zinc-200">{{ e.institution }}</span>
                          <span v-if="e.program_name" class="text-zinc-400 dark:text-zinc-500"> · {{ e.program_name }}</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                <!-- Card footer: meta + buttons -->
                <div class="cv-card-footer">
                  <div class="cv-card-meta">
                    <div class="cv-status-wrap" @click.stop>
                      <button
                        type="button"
                        :class="[statusClass(cv.status), 'cv-status-btn']"
                        :disabled="updatingStatusCvId === cv.id"
                        @click="toggleStatusMenu(cv.id)"
                        title="Change status"
                      >
                        {{ statusLabel(cv.status) }}
                        <svg class="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      <div v-if="openStatusMenuId === cv.id" class="cv-status-menu">
                        <button
                          v-for="option in statusOptions"
                          :key="option.value"
                          type="button"
                          class="cv-status-menu-item"
                          :class="{ 'cv-status-menu-item--active': cv.status === option.value }"
                          @click="setCvStatus(cv, option.value)"
                        >
                          <span class="cv-status-dot" :class="`cv-status-dot--${option.value}`"></span>
                          {{ option.label }}
                          <svg v-if="cv.status === option.value" class="w-3 h-3 ml-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                          </svg>
                        </button>
                      </div>
                    </div>
                    <span class="cv-meta-dot">·</span>
                    <span class="cv-date">{{ relativeDate(cv.updated_at) }}</span>
                    <template v-if="cv.last_exported">
                      <span class="cv-meta-dot">·</span>
                      <span class="cv-downloads">exported {{ relativeDate(cv.last_exported) }}</span>
                    </template>
                  </div>
                  <div class="cv-card-btns">
                    <button @click.stop="openDuplicateModal(cv.id)" class="btn-outline-sm">Duplicate</button>
                    <button @click="openDownloadModal(cv.id)" class="btn-primary-sm" :disabled="downloadingCvId === cv.id">
                      <span v-if="downloadingCvId === cv.id">
                        <svg class="inline w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      </span>
                      <span v-else>Download</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </div>
      </main>

      <!-- ── RIGHT SIDEBAR ── -->
      <aside class="dash-sidebar">

        <!-- Actions -->
        <div class="sidebar-actions">
          <button @click="createNewCv" class="action-gold">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M12 4v16m8-8H4" />
            </svg>
            New CV
          </button>
          <router-link to="/profile" class="action-secondary">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            Edit Profile
          </router-link>
          <button @click="isImportModalOpen = true" class="action-secondary">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
            </svg>
            Import CV
          </button>
          <button class="action-secondary" disabled>
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            References
            <span class="action-coming-soon">soon</span>
          </button>
        </div>

        <!-- CV usage (free plan only) -->
        <div v-if="!isPro" class="border border-zinc-100 rounded-xl p-4 hover:border-primary transition-colors">
          <div class="flex items-center justify-between mb-2">
            <div class="flex items-center gap-1.5">
              <span class="w-0.5 h-3.5 rounded-full bg-primary flex-shrink-0" />
              <span class="text-xs font-semibold uppercase tracking-widest text-zinc-400">Free Plan</span>
            </div>
            <span class="text-xs font-semibold text-zinc-900">{{ cvCount }} / {{ cvLimit }}</span>
          </div>
          <div class="h-1 bg-zinc-100 rounded-full overflow-hidden mb-3">
            <div
              class="h-full bg-primary rounded-full transition-all"
              :style="{ width: `${Math.min(100, (cvCount / cvLimit) * 100)}%` }"
            />
          </div>
          <p class="text-xs text-zinc-500 leading-relaxed mb-3">
            {{ cvSlotsRemaining === 0 ? 'No CV slots remaining.' : `${cvSlotsRemaining} CV slot${cvSlotsRemaining === 1 ? '' : 's'} remaining.` }}
            Upgrade for unlimited CVs.
          </p>
          <button
            class="w-full bg-primary text-white text-xs font-medium py-2 rounded-lg hover:opacity-90 transition-opacity"
            @click="$router.push('/pricing')"
          >
            Upgrade to Pro
          </button>
        </div>

        <!-- Master profile snapshot -->
        <div class="sidebar-profile">
          <div class="sidebar-section-header">
            <span class="sidebar-section-title">Master Profile</span>
            <router-link to="/profile" class="sidebar-section-link">Edit →</router-link>
          </div>

          <!-- Empty profile nudge -->
          <div v-if="profileIsEmpty" class="profile-empty-nudge">
            <p class="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
              Your profile is empty. Add your experience and education once — then reuse it across all your CVs.
            </p>
            <router-link to="/profile" class="action-primary mt-3 text-xs py-1.5">
              Set up profile
            </router-link>
          </div>

          <template v-else>
            <!-- Experience -->
            <div v-if="masterProfile.experience_count > 0" class="profile-block">
              <div class="profile-block-header">
                <svg class="w-3.5 h-3.5 text-zinc-300 dark:text-zinc-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m8 0V6a2 2 0 012 2v6M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                </svg>
                <span class="profile-block-label">Experience</span>
                <span class="profile-block-count">{{ masterProfile.experience_count }}</span>
              </div>
              <ul class="profile-preview-list">
                <li v-for="e in masterProfile.experience_preview" :key="e.company" class="profile-preview-item">
                  <span class="profile-preview-text">
                    <span class="font-medium text-zinc-700 dark:text-zinc-200">{{ e.job_title }}</span>
                    <span class="text-zinc-400 dark:text-zinc-500"> · {{ e.company }}</span>
                  </span>
                  <span v-if="e.variant_count > 0" class="variant-badge" :title="`${e.variant_count} alternative${e.variant_count === 1 ? '' : 's'}`">+{{ e.variant_count }}</span>
                </li>
              </ul>
            </div>

            <!-- Education -->
            <div v-if="masterProfile.education_count > 0" class="profile-block">
              <div class="profile-block-header">
                <svg class="w-3.5 h-3.5 text-zinc-300 dark:text-zinc-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                </svg>
                <span class="profile-block-label">Education</span>
                <span class="profile-block-count">{{ masterProfile.education_count }}</span>
              </div>
              <ul class="profile-preview-list">
                <li v-for="e in masterProfile.education_preview" :key="e.institution" class="profile-preview-item">
                  <span class="profile-preview-text">
                    <span class="font-medium text-zinc-700 dark:text-zinc-200">{{ e.institution }}</span>
                    <span v-if="e.program_name" class="text-zinc-400 dark:text-zinc-500"> · {{ e.program_name }}</span>
                  </span>
                  <span v-if="e.variant_count > 0" class="variant-badge" :title="`${e.variant_count} alternative${e.variant_count === 1 ? '' : 's'}`">+{{ e.variant_count }}</span>
                </li>
              </ul>
            </div>

            <!-- Activities -->
            <div v-if="masterProfile.activities_count > 0" class="profile-block">
              <div class="profile-block-header">
                <svg class="w-3.5 h-3.5 text-zinc-300 dark:text-zinc-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span class="profile-block-label">Activities</span>
                <span class="profile-block-count">{{ masterProfile.activities_count }}</span>
              </div>
              <ul class="profile-preview-list">
                <li v-for="a in masterProfile.activities_preview" :key="a.organization" class="profile-preview-item">
                  <span class="profile-preview-text">
                    <span class="font-medium text-zinc-700 dark:text-zinc-200">{{ a.role || a.organization }}</span>
                    <span v-if="a.role && a.organization" class="text-zinc-400 dark:text-zinc-500"> · {{ a.organization }}</span>
                  </span>
                  <span v-if="a.variant_count > 0" class="variant-badge" :title="`${a.variant_count} alternative${a.variant_count === 1 ? '' : 's'}`">+{{ a.variant_count }}</span>
                </li>
              </ul>
            </div>

            <!-- Skills -->
            <div v-if="masterProfile.skills_count > 0" class="profile-block">
              <div class="profile-block-header">
                <svg class="w-3.5 h-3.5 text-zinc-300 dark:text-zinc-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
                <span class="profile-block-label">Skills</span>
                <span class="profile-block-count">{{ masterProfile.skills_count }} categories</span>
              </div>
              <div v-if="masterProfile.skills_preview && masterProfile.skills_preview.length" class="profile-skills-tags">
                <span v-for="s in masterProfile.skills_preview" :key="s" class="skill-tag">{{ s }}</span>
              </div>
            </div>
          </template>
        </div>
      </aside>

    </div>

    <!-- Modals -->
    <CvMetadataEditModal :is-open="isEditModalOpen" :cv-id="editingCvId" @close="closeEditModal" @updated="fetchDashboardData" />
    <CvDuplicateModal :is-open="isDuplicateModalOpen" :cv-id="duplicatingCvId" @close="closeDuplicateModal" @duplicated="handleCvDuplicated" />
    <CvDeleteConfirmModal :is-open="isDeleteConfirmModalOpen" :cv-data="deletingCvData" @close="closeDeleteConfirmModal" @confirm="handleCvDeleted" />
    <CvCreationWizard :is-open="isWizardOpen" @close="isWizardOpen = false" />
    <ImportCvModal :is-open="isImportModalOpen" @close="isImportModalOpen = false" @imported="fetchDashboardData" />
    <CvDownloadOptionsModal
      :is-open="isDownloadModalOpen"
      :cv-id="downloadModalCvId"
      @close="closeDownloadModal"
      @download-cv-only="downloadCv(downloadModalCvId)"
      @download-cv-with-references="downloadCvWithRefs(downloadModalCvId)"
    />
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import logoImage from '../assets/icons/cv-tool-gold.png'
import CvMetadataEditModal from './CvMetadataEditModal.vue'
import CvDuplicateModal from './CvDuplicateModal.vue'
import CvDeleteConfirmModal from './CvDeleteConfirmModal.vue'
import CvCreationWizard from './CvCreationWizard.vue'
import ImportCvModal from './ImportCvModal.vue'
import CvDownloadOptionsModal from './CvDownloadOptionsModal.vue'
import { useCvDirectDownload } from '../composables/useCvDirectDownload'
import { useAuth } from '../composables/useAuth.js'
import { useSubscription } from '../composables/useSubscription.js'
import { reportUpgradeRequired } from '../services/ApiService.js'
import UserDropdown from './UserDropdown.vue'
import '../styles/dashboard-styles.css'

export default {
  name: 'Dashboard',
  components: { CvMetadataEditModal, CvDuplicateModal, CvDeleteConfirmModal, CvCreationWizard, ImportCvModal, CvDownloadOptionsModal, UserDropdown },
  setup() {
    const router = useRouter()
    const isDarkMode = ref(false)
    const isLoading = ref(true)
    const error = ref(null)
    const userName = ref('')
    const userEmail = ref('')
    const totalCvs = ref(0)
    const cvGroups = ref([])
    const masterProfile = ref({})

    const { downloadCvDirectly, downloadCvWithReferences } = useCvDirectDownload()
    // Shared logout: resets the cached auth state and redirects to the
    // landing page; a local signout + router.push would bounce back here
    // because the router guard still sees the stale authenticated state.
    const { handleLogout } = useAuth()
    const { isPro, cvLimit, cvCount, cvSlotsRemaining, cvLimitReached, setSubscription } = useSubscription()

    const isEditModalOpen = ref(false)
    const editingCvId = ref(null)
    const isDuplicateModalOpen = ref(false)
    const duplicatingCvId = ref(null)
    const isDeleteConfirmModalOpen = ref(false)
    const deletingCvData = ref(null)
    const downloadingCvId = ref(null)
    const isWizardOpen = ref(false)
    const isImportModalOpen = ref(false)
    const isDownloadModalOpen = ref(false)
    const downloadModalCvId = ref(null)

    const showConfirmationBanner = ref(false)
    const resendMessage = ref('')
    const isResendingConfirmation = ref(false)

    const profileIsEmpty = computed(() =>
      masterProfile.value.experience_count === 0 &&
      masterProfile.value.education_count === 0 &&
      masterProfile.value.skills_count === 0 &&
      masterProfile.value.activities_count === 0
    )

    // Flatten all CV groups into a single list sorted by last edited
    const allCvs = computed(() => {
      return cvGroups.value
        .flatMap(g => g.cvs)
        .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
    })

    // Group CVs by creation month for section labels
    const cvsByMonth = computed(() => {
      const sorted = [...allCvs.value].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      const groups = []
      const seen = {}
      for (const cv of sorted) {
        const label = new Date(cv.created_at).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
        if (!seen[label]) {
          seen[label] = { label, cvs: [] }
          groups.push(seen[label])
        }
        seen[label].cvs.push(cv)
      }
      return groups
    })

    const statusLabel = (s) => s === 'published' ? 'Published' : s === 'archived' ? 'Archived' : 'Draft'
    const statusClass = (s) => {
      if (s === 'published') return 'cv-status cv-status--published'
      if (s === 'archived') return 'cv-status cv-status--archived'
      return 'cv-status cv-status--draft'
    }

    const statusOptions = [
      { value: 'draft', label: 'Draft' },
      { value: 'published', label: 'Published' },
      { value: 'archived', label: 'Archived' }
    ]
    const openStatusMenuId = ref(null)
    const updatingStatusCvId = ref(null)

    const toggleStatusMenu = (id) => {
      openStatusMenuId.value = openStatusMenuId.value === id ? null : id
    }

    const closeStatusMenu = () => { openStatusMenuId.value = null }

    const setCvStatus = async (cv, status) => {
      closeStatusMenu()
      if (cv.status === status) return

      const previousStatus = cv.status
      cv.status = status
      updatingStatusCvId.value = cv.id
      try {
        const res = await fetch(`/api/cvs/${cv.id}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-CSRF-Token': getCSRFToken()
          },
          body: JSON.stringify({ cv: { status } })
        })
        const data = await res.json()
        if (!res.ok || !data.success) throw new Error(data.message || 'Failed to update status')
      } catch (err) {
        cv.status = previousStatus
        error.value = err.message
      } finally {
        updatingStatusCvId.value = null
      }
    }

    const relativeDate = (dateStr) => {
      if (!dateStr) return ''
      const diff = Date.now() - new Date(dateStr)
      const days = Math.floor(diff / 86400000)
      if (days === 0) return 'today'
      if (days === 1) return 'yesterday'
      if (days < 7) return `${days}d ago`
      if (days < 30) return `${Math.floor(days / 7)}w ago`
      return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
    }

    const getCSRFToken = () =>
      document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')

    const fetchDashboardData = async () => {
      try {
        isLoading.value = true
        error.value = null
        const res = await fetch('/api/users/dashboard', { headers: { Accept: 'application/json' } })
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const data = await res.json()
        if (!data.success) throw new Error(data.message || 'Failed to load dashboard')
        userName.value = data.user.full_name
        userEmail.value = data.user.email
        totalCvs.value = data.total_cvs
        cvGroups.value = data.cv_groups
        masterProfile.value = data.master_profile || {}
        if (data.user.email_confirmed === false) showConfirmationBanner.value = true
        if (data.user?.subscription) setSubscription(data.user.subscription)
      } catch (err) {
        error.value = err.message
      } finally {
        isLoading.value = false
      }
    }

    const createNewCv = () => {
      // Don't let free users fill in the whole wizard just to hit the 402
      if (cvLimitReached.value) {
        reportUpgradeRequired({ cv_count: cvCount.value, cv_limit: cvLimit.value })
        return
      }
      isWizardOpen.value = true
    }
    const editCv = (id) => router.push(`/cv-creator/${id}`)

    const openEditModal = (id) => { editingCvId.value = id; isEditModalOpen.value = true }
    const closeEditModal = () => { isEditModalOpen.value = false; editingCvId.value = null }

    const openDuplicateModal = (id) => { duplicatingCvId.value = id; isDuplicateModalOpen.value = true }
    const closeDuplicateModal = () => { isDuplicateModalOpen.value = false; duplicatingCvId.value = null }
    const handleCvDuplicated = async (newCv) => {
      closeDuplicateModal()
      await fetchDashboardData()
      router.push(`/cv-creator/${newCv.id}`)
    }

    const openDeleteConfirmModal = (cv) => { deletingCvData.value = cv; isDeleteConfirmModalOpen.value = true }
    const closeDeleteConfirmModal = () => { isDeleteConfirmModalOpen.value = false; deletingCvData.value = null }
    const handleCvDeleted = async (cvId) => {
      const id = cvId || deletingCvData.value?.id
      closeDeleteConfirmModal()
      await fetch(`/api/cvs/${id}`, {
        method: 'DELETE',
        headers: { 'X-CSRF-Token': getCSRFToken(), Accept: 'application/json' }
      })
      await fetchDashboardData()
    }

    const openDownloadModal = (id) => { downloadModalCvId.value = id; isDownloadModalOpen.value = true }
    const closeDownloadModal = () => { isDownloadModalOpen.value = false }

    const downloadCv = async (id) => {
      downloadingCvId.value = id
      try { await downloadCvDirectly(id) } finally { downloadingCvId.value = null }
    }

    const downloadCvWithRefs = async (id) => {
      downloadingCvId.value = id
      try { await downloadCvWithReferences(id) } finally { downloadingCvId.value = null }
    }

    const resendConfirmationEmail = async () => {
      isResendingConfirmation.value = true
      try {
        const res = await fetch('/api/users/resend_confirmation', {
          method: 'POST',
          headers: { 'X-CSRF-Token': getCSRFToken(), Accept: 'application/json' }
        })
        const data = await res.json()
        resendMessage.value = data.success ? 'Email sent!' : data.message
      } catch { resendMessage.value = 'Failed to send. Please try again.' }
      finally { isResendingConfirmation.value = false }
    }

    onMounted(async () => {
      const mq = window.matchMedia('(prefers-color-scheme: dark)')
      isDarkMode.value = mq.matches
      mq.addEventListener('change', (e) => { isDarkMode.value = e.matches })
      document.addEventListener('click', closeStatusMenu)
      await fetchDashboardData()
    })

    onUnmounted(() => {
      document.removeEventListener('click', closeStatusMenu)
    })

    return {
      isDarkMode, isLoading, error, userName, userEmail, statusLabel, statusClass,
      statusOptions, openStatusMenuId, updatingStatusCvId, toggleStatusMenu, setCvStatus,
      totalCvs, allCvs, cvsByMonth, masterProfile, profileIsEmpty,
      isEditModalOpen, editingCvId,
      isDuplicateModalOpen, duplicatingCvId,
      isDeleteConfirmModalOpen, deletingCvData,
      downloadingCvId, isWizardOpen, isImportModalOpen,
      showConfirmationBanner, resendMessage, isResendingConfirmation,
      logoImage, relativeDate,
      fetchDashboardData, handleLogout,
      createNewCv, editCv,
      openEditModal, closeEditModal,
      openDuplicateModal, closeDuplicateModal, handleCvDuplicated,
      openDeleteConfirmModal, closeDeleteConfirmModal, handleCvDeleted,
      isDownloadModalOpen, downloadModalCvId, openDownloadModal, closeDownloadModal,
      downloadCv, downloadCvWithRefs, resendConfirmationEmail,
      isPro, cvLimit, cvCount, cvSlotsRemaining,
    }
  }
}
</script>

<style scoped>
.confirmation-banner { background: #fffbeb; border-bottom: 1px solid #fde68a; }
.dark .confirmation-banner { background: #292524; border-bottom-color: #78350f; }
.confirmation-banner-content { max-width: 1280px; margin: 0 auto; padding: 0.625rem 1.5rem; display: flex; align-items: center; gap: 0.75rem; }
.confirmation-banner-text { flex: 1; font-size: 0.875rem; color: #92400e; }
.dark .confirmation-banner-text { color: #fcd34d; }
.confirmation-banner-btn { font-size: 0.8125rem; font-weight: 600; color: #d97706; background: none; border: none; cursor: pointer; white-space: nowrap; padding: 0.25rem 0.5rem; border-radius: 4px; }
.confirmation-banner-btn:hover:not(:disabled) { background: #fef3c7; }
.confirmation-banner-feedback { font-size: 0.8125rem; color: #92400e; }
.dark .confirmation-banner-feedback { color: #fcd34d; }
.confirmation-banner-dismiss { color: #9ca3af; background: none; border: none; cursor: pointer; padding: 0.25rem; border-radius: 4px; display: flex; align-items: center; }
.confirmation-banner-dismiss:hover { color: #6b7280; }
</style>
